import { connect } from 'react-redux';
import { getEnvironment } from '~/store/selectors';
import { actions as profileSidebarActions } from '~/profile-sidebar/reducer';
import { actions as composerPopoverActions } from '~/composer-popover';
import { actions as analyticsActions } from '~/analytics-middleware/actions';
import { SEGMENT_NAMES } from '~/constants';
import { getURL } from '~/server/formatters';
import { profileChildTabPages, calendarMonth, calendarWeek } from '~/routes';

import { actions } from './reducer';
import {
  formatPostLists,
  openCalendarWindow,
  isScheduleSlotsAvailable,
} from './util';
import QueuedPosts from './components/QueuedPosts';

export default connect(
  (state, ownProps) => {
    const { profileId } = ownProps;
    const queue = state.queue.byProfileId[profileId];
    const profileData = state.profileSidebar.profiles.find(
      (p) => p.id === ownProps.profileId
    );

    const queuePostsArray =
      queue &&
      queue.posts &&
      Object.keys(queue.posts).map((key) => {
        return queue.posts[key];
      });

    const { days } = state.postingSchedule;
    const shouldDisplaySingleSlots =
      days.length > 0 &&
      days.every((day) => day.times.length === 0 || day.paused);

    const pausedDays = days
      .filter((day) => day.paused === true)
      .map((day) => day.dayName);

    const hasAtLeastOneReminderPost =
      queuePostsArray &&
      queuePostsArray.some(
        (post) => post.postDetails && post.postDetails.isInstagramReminder
      );

    if (queue && profileData) {
      const { isDisconnected } = state.profileSidebar.selectedProfile;
      const isInstagramProfile = profileData.type === 'instagram';
      const isTwitterProfile = profileData.type === 'twitter';
      const isLinkedInProfile = profileData.type === 'linkedin';
      const isMastodonProfile = profileData.type === 'mastodon';
      const noMastodonChannelsConnected =
        state.profileSidebar?.profiles.filter(
          (profile) => profile.service === 'mastodon'
        ).length === 0;

      const showMastodonPromoBanner =
        (isTwitterProfile || isLinkedInProfile) && noMastodonChannelsConnected;

      return {
        loading: queue.loading,
        loadingMore: queue.loadingMore,
        moreToLoad: queue.moreToLoad,
        page: queue.page,
        items: formatPostLists({
          isManager: profileData.isManager,
          posts: queue.posts,
          scheduleSlotsEnabled: true,
          schedules: profileData.schedules,
          profileTimezone: profileData.timezone,
          weekStartsOnMonday: state.user.week_starts_monday,
          weeksToShow: queue.page + 1,
          hasTwentyFourHourTimeFormat: state.user.hasTwentyFourHourTimeFormat,
          profileService: profileData.service,
          shouldDisplaySingleSlots,
          pausedDays,
        }),
        scheduleSlotsIsAvailable: isScheduleSlotsAvailable(
          profileData.schedules
        ),
        draggingEnabled: !profileData.paused,
        showEmptyQueueMessage: false, // @todo: Show this if they have no slots?
        showComposer: state.composerPopover.showComposer,
        environment: getEnvironment(state),
        editMode: state.composerPopover.editMode,
        editingPostId: state.composerPopover.editingPostId,
        subprofiles: profileData.subprofiles || [],
        paused: profileData.paused,
        isManager: profileData.isManager,
        hasPushNotifications: profileData.hasPushNotifications,
        hasFirstCommentFlip:
          state.organizations.selected?.hasFirstCommentFeature,
        hasCampaignsFeature: state.organizations.selected?.hasCampaignsFeature,
        shouldRenderCalendarButtons: true,
        shouldDisplaySingleSlots,
        shouldDisplayDisconnectedBanner: isDisconnected,
        shouldDisplayRemindersBanner:
          !isDisconnected &&
          !profileData.hasPushNotifications &&
          isInstagramProfile &&
          hasAtLeastOneReminderPost,
        shouldDisplayTimezone: profileData.isManager,
        profileTimezone: profileData.timezone,
        showMastodonPromoBanner,
        composerType: state.queue.composerType,
        isMastodonProfile,
      };
    }
    return {
      items: [],
    };
  },

  (dispatch, ownProps) => ({
    onEditClick: (post) => {
      dispatch(composerPopoverActions.handleEditClick(post.post.id));
    },
    onEmptySlotClick: (post) => {
      dispatch(
        composerPopoverActions.handleEmptySlotClick({
          emptySlotData: post,
          profileId: ownProps.profileId,
          cta: SEGMENT_NAMES.CREATE_A_POST_SLOT,
        })
      );
    },
    onDeleteConfirmClick: (post) => {
      dispatch(
        actions.handleDeleteConfirmClick({
          post: post.post,
          profileId: ownProps.profileId,
        })
      );
    },
    onRequeueClick: (post) => {
      dispatch(
        actions.handleRequeue({
          post: post.post,
          profileId: ownProps.profileId,
        })
      );
    },
    onCampaignTagClick: (campaignId) => {
      dispatch(
        actions.handleCampaignTagClick({
          campaignId,
        })
      );
    },
    onShareNowClick: (post) => {
      dispatch(
        actions.handleShareNowClick({
          post: post.post,
          profileId: ownProps.profileId,
        })
      );
    },
    onDropPost: (id, timestamp, day) => {
      dispatch(actions.onDropPost(id, timestamp, day, ownProps.profileId));
    },
    onSwapPosts: (postSource, postTarget) => {
      dispatch(actions.onSwapPosts(postSource, postTarget, ownProps.profileId));
    },
    onUnpauseClick: () => {
      dispatch(
        profileSidebarActions.onUnpauseClick({ profileId: ownProps.profileId })
      );
    },
    onCreatePostClick: () => {
      dispatch(
        composerPopoverActions.handleCreatePostClick({
          cta: SEGMENT_NAMES.CREATE_A_POST,
          selectedProfileId: ownProps.profileId,
          shouldResetComposerData: true,
        })
      );
    },
    onAutoOpenComposer: ({ selectedProfileIds }) => {
      dispatch(
        composerPopoverActions.handleOpenComposerFromQueryParams({
          cta: SEGMENT_NAMES.CREATE_A_POST_VIA_AUTO_OPEN_COMPOSER,
          selectedProfileIds: selectedProfileIds || [ownProps.profileId],
          shouldResetComposerData: true,
        })
      );
    },
    onComposerCreateSuccess: () => {
      dispatch(composerPopoverActions.hideComposer());
      dispatch(actions.handleUpdateComposerType({ composerType: 'queue' }));
    },
    onConfirmDismissComposer: () => {
      dispatch(composerPopoverActions.hideComposer());
      dispatch(actions.handleUpdateComposerType({ composerType: 'queue' }));
    },
    onHideInstagramModal: () => {
      dispatch(actions.handleHideInstagramModal());
    },
    onSetRemindersClick: ({ type }) => {
      let cta = '';
      if (type === 'banner') {
        cta = SEGMENT_NAMES.REMINDERS_BANNER;
      }
      if (type === 'post') {
        cta = SEGMENT_NAMES.REMINDERS_POST;
      }
      window.location.assign(
        `${getURL.getRemindersURL({
          profileId: ownProps.profileId,
          cta,
          nextUrl: `profile/${ownProps.profileId}/tab/queue`,
        })}`
      );
    },
    onCalendarClick: (weekOrMonth) => {
      if (weekOrMonth === 'week') {
        dispatch(
          calendarWeek.goToWithChannel({ channelId: ownProps.profileId })
        );
      }
      if (weekOrMonth === 'month') {
        dispatch(
          calendarMonth.goToWithChannel({ channelId: ownProps.profileId })
        );
      }
    },
    onOldCalendarClick: (weekOrMonth) => {
      const metadata = {
        source: 'queue',
        calendarVersion: 'classic',
        view: weekOrMonth,
      };
      dispatch(analyticsActions.trackEvent('Calendar Opened', metadata));
      if (weekOrMonth === 'week' || weekOrMonth === 'month') {
        openCalendarWindow(ownProps.profileId, weekOrMonth);
      }
    },
    onTimezoneClick: () => {
      dispatch(
        profileChildTabPages.goTo({
          profileId: ownProps.profileId,
          tabId: 'settings',
          childTabId: 'postingSchedule',
        })
      );
    },
  })
)(QueuedPosts);

export { default as reducer, actions, actionTypes } from './reducer';
export { default as middleware } from './middleware';
